import { FC } from 'react';
import { TbChartBar } from 'react-icons/tb';
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { CustomRechartsUsageAnalyticsTooltip, EmptySection } from '@/shared';
import AnalyticsSectionCard from '@/shared/components/cards/AnalyticsSectionCard';
import { dayjs } from '@/utilities';

import { UsageEventTimelineSkeleton } from '../UsageAnalyticsSkeletons';

interface UsageTimelineChartProps {
    loading?: boolean;
    resourceColorMap: Record<string, string>;
    resourceFillMap: Record<string, string>;
    resourceIds: string[];
    resourceLabelMap: Record<string, string>;
    transformedChartData: Record<string, string | number>[];
}

const UsageTimelineChart: FC<UsageTimelineChartProps> = ({
    loading = false,
    resourceColorMap,
    resourceFillMap,
    resourceIds,
    resourceLabelMap,
    transformedChartData,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <AnalyticsSectionCard
            ariaLabel='Workspace event timeline'
            contentStyle={{ padding: '0.75rem' }}
            description='Compare event volume across workspace resources for the selected period.'
            icon={<TbChartBar />}
            title='Event Timeline'
        >
            {loading ? (
                <UsageEventTimelineSkeleton />
            ) : transformedChartData.length && resourceIds.length ? (
                <ResponsiveContainer aspect={2} width='100%'>
                    <BarChart
                        data={transformedChartData}
                        margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                        style={{ backgroundColor: token.colorBgContainer }}
                        barGap={8}
                        barCategoryGap='20%'
                    >
                        {resourceIds.map((resourceId) => (
                            <Bar
                                key={resourceId}
                                dataKey={resourceId}
                                name={resourceLabelMap[resourceId]}
                                fill={`url(#gradient-${resourceId})`}
                                stroke={resourceColorMap[resourceId]}
                                radius={[2, 2, 0, 0]}
                                barSize={16}
                            />
                        ))}

                        <defs>
                            {resourceIds.map((resourceId) => (
                                <linearGradient
                                    id={`gradient-${resourceId}`}
                                    x1='0'
                                    y1='0'
                                    x2='0'
                                    y2='1'
                                    key={resourceId}
                                >
                                    <stop offset='1%' stopColor={resourceColorMap[resourceId]} stopOpacity={0.25} />
                                    <stop offset='99%' stopColor={resourceColorMap[resourceId]} stopOpacity={0} />
                                </linearGradient>
                            ))}
                        </defs>

                        <CartesianGrid stroke={token.colorBorder} strokeDasharray='4 4' />
                        <XAxis
                            dataKey='date'
                            axisLine={{ stroke: token.colorBorder }}
                            tick={{ fill: token.colorTextTertiary }}
                            tickFormatter={(value) => dayjs(value).format('MMM Do, YYYY')}
                        />
                        <YAxis
                            orientation='right'
                            axisLine={{ stroke: token.colorBorder }}
                            tick={{ fill: token.colorTextTertiary }}
                            width={48}
                            tickLine={false}
                        />
                        <Tooltip
                            cursor={{ fill: token.colorFillQuaternary }}
                            content={(props) => (
                                <CustomRechartsUsageAnalyticsTooltip
                                    {...props}
                                    resourceLabelMap={resourceLabelMap}
                                    resourceColorMap={resourceColorMap}
                                    resourceFillMap={resourceFillMap}
                                />
                            )}
                        />
                        <ReferenceLine y={0} stroke={token.colorBorder} />
                    </BarChart>
                </ResponsiveContainer>
            ) : (
                <EmptySection
                    description='Workspace event volume will appear here as activity is recorded.'
                    height='13rem'
                    icon={<TbChartBar size='1.25rem' />}
                    label='No Event Timeline Data'
                />
            )}
        </AnalyticsSectionCard>
    );
};

export default UsageTimelineChart;
